/*
 * File: ProcedureCodeSearchForm.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles searching for a procedure code
 */
import React, { useState } from "react";
import UISearch from "components/ui/UISearch";
import ProcedureCodeSearchResults from "./ProcedureCodeSearchResults";
import { observer } from "mobx-react";
import FeeScheduleSelectField from "../FeeScheduleSelectField";
import { Theme, withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import UIProgress from "components/ui/UIProgress";
import { WithStyles } from "@material-ui/styles";
import ChargeModel from "services/patients/post_charges/store/ChargeModel";
import UISearchStore from "services/patients/post_charges/store/UISearchStore";
import { Instance } from "mobx-state-tree";

const styles = (theme: Theme) => ({
  errorMessage: {
    padding: theme.spacing(2)
  }
});

interface Props extends WithStyles<typeof styles> {
  parentCallBack: () => void;
  chargeModel: Instance<typeof ChargeModel>;
  uiSearchStore: Instance<typeof UISearchStore>;
}

const ProcedureCodeSearchForm = ({
  classes,
  parentCallBack,
  chargeModel,
  uiSearchStore
}: Props) => {
  const [
    showFeeScheduledRequiredError,
    setShowFeeScheduledRequiredError
  ] = useState(false);

  const procedureCodeSearchSubmit = () => {
    if (uiSearchStore.feeScheduleId === undefined) {
      setShowFeeScheduledRequiredError(true);
    } else {
      uiSearchStore.searchProcedureCode();
    }
  };

  const formCallBack = () => {
    uiSearchStore.setProcedureCodes([]);
    parentCallBack();
  };

  const setFeeScheduleInStore = (feeScheduleId: number) => {
    uiSearchStore.setFeeScheduleId(feeScheduleId);
    setShowFeeScheduledRequiredError(false);
  };

  const saveKeyword = (keyword: string | undefined) => {
    //If keyword has been cleared out, call clearProcedureCodeSearch
    if (keyword === undefined || keyword.length === 0) {
      uiSearchStore.clearProcedureCodeSearch();
    } else {
      uiSearchStore.setProcedureCodeKeyword(keyword);
    }
  };
  return (
    <React.Fragment>
      <div className="sub-toolbar">
        <FeeScheduleSelectField
          showFeeScheduledRequiredError={showFeeScheduledRequiredError}
          setFeeScheduleInStore={setFeeScheduleInStore}
          selectedFeeScheduleId={uiSearchStore.feeScheduleId}
        />
        <UISearch
          keyword={uiSearchStore.procedureCodeSearchKeyword}
          parentOnChange={saveKeyword}
          parentOnSubmit={procedureCodeSearchSubmit}
          placeholder={"Search for a procedure code..."}
        />
      </div>
      {uiSearchStore.loading && <UIProgress />}
      {uiSearchStore.searched &&
        uiSearchStore.procedureCodes &&
        uiSearchStore.procedureCodes.length === 0 && (
          <Typography className={classes.errorMessage} align="center">
            There are no procedure codes matching your search. Search again or
            exit the search.
          </Typography>
        )}

      {uiSearchStore.searched && uiSearchStore.procedureCodes && (
        <ProcedureCodeSearchResults
          searchResults={uiSearchStore.procedureCodes}
          parentCallBack={formCallBack}
          chargeModel={chargeModel}
        />
      )}
    </React.Fragment>
  );
};

export default withStyles(styles)(observer(ProcedureCodeSearchForm));
